import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Alert,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTemplateEditor } from './hooks/useTemplateEditor';
import {
  TemplateEditorHeader,
  TemplateForm,
  ModelConfiguration,
  TemplateActions,
} from './components';
import TemplateExecutionPanel from './components/TemplateExecutionPanel';
import AITemplateGeneratorDialog from './components/AITemplateGeneratorDialog';
import ContentCheckPolicyManagerDialog from '../ContentCheckPolicyManager/ContentCheckPolicyManagerDialog';
import TemplatePoliciesList from './components/TemplatePoliciesList';

/**
 * TemplateEditor Component
 *
 * Main template editor component that renders either a create or edit form.
 * Uses modular components for better maintainability and follows the same pattern as PromptManagerDashboard.
 *
 * Features:
 * - Template creation and editing
 * - AI-powered template generation
 * - Model configuration
 * - Version management
 * - Form validation and error handling
 */
const TemplateEditor = () => {
  const { templateId, versionId } = useParams();
  const [aiGeneratorDialogOpen, setAiGeneratorDialogOpen] = useState(false);
  const [securityPoliciesDialogOpen, setSecurityPoliciesDialogOpen] =
    useState(false);
  const {
    // State
    loading,
    saving,
    updatingMetadata,
    error,
    success,
    aiPrompt,
    generatingTemplate,
    formData,
    newTag,
    showModelConfig,
    providers,
    providerModels,
    loadingProviders,
    isEditMode,

    // Setters
    setAiPrompt,
    setNewTag,
    setShowModelConfig,
    setSuccess,

    // Handlers
    handleInputChange,
    handleModelConfigChange,
    handleAddTag,
    handleRemoveTag,
    handleTagKeyPress,
    handleSave,
    handleUpdateMetadata,
    handleUpdateDraft,
    handleCancel,
    currentVersionStatus,
    handleGenerateTemplate,

    // Helper functions
    getModelsForProvider,
    getDefaultModelForProvider,
  } = useTemplateEditor();

  // Close dialog on successful template generation
  useEffect(() => {
    if (success && !generatingTemplate && aiGeneratorDialogOpen) {
      // Small delay to let user see the success message
      const timer = setTimeout(() => {
        setAiGeneratorDialogOpen(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [success, generatingTemplate, aiGeneratorDialogOpen]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <TemplateEditorHeader
        isEditMode={isEditMode}
        versionId={versionId}
        onCancel={handleCancel}
      />

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Versioning Info for Edit Mode */}
      {isEditMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Versioning System:</strong> When you edit a template, a new
            version is created automatically. This preserves the history of your
            template changes and allows you to track improvements over time.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Form */}
        <Grid size={{ xs: 12, md: 8 }}>
          <TemplateForm
            formData={formData}
            isEditMode={isEditMode}
            versionId={versionId}
            newTag={newTag}
            handleInputChange={handleInputChange}
            handleAddTag={handleAddTag}
            handleRemoveTag={handleRemoveTag}
            handleTagKeyPress={handleTagKeyPress}
            setNewTag={setNewTag}
            onOpenAIGenerator={() => {
              setSuccess(null); // Clear success state when opening dialog
              setAiGeneratorDialogOpen(true);
            }}
          />
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Security Policies List - Only in edit mode */}
          {isEditMode && templateId && templateId !== 'new' && (
            <TemplatePoliciesList
              templateId={templateId}
              onManagePolicies={() => setSecurityPoliciesDialogOpen(true)}
            />
          )}

          {/* Model Configuration */}
          <ModelConfiguration
            formData={formData}
            providers={providers}
            providerModels={providerModels}
            loadingProviders={loadingProviders}
            showModelConfig={showModelConfig}
            handleModelConfigChange={handleModelConfigChange}
            getModelsForProvider={getModelsForProvider}
            getDefaultModelForProvider={getDefaultModelForProvider}
            setShowModelConfig={setShowModelConfig}
          />

          {/* Test Execution Panel - Only in edit mode */}
          {isEditMode && templateId && templateId !== 'new' && (
            <TemplateExecutionPanel
              templateId={templateId}
              currentVersion={undefined} // Will be determined from versions
              templateContent={formData.template}
              isEditMode={isEditMode}
              templateName={formData.name}
            />
          )}

          {/* Actions */}
          <TemplateActions
            saving={saving}
            updatingMetadata={updatingMetadata}
            isEditMode={isEditMode}
            templateId={templateId}
            versionId={versionId}
            currentVersionStatus={currentVersionStatus}
            handleSave={handleSave}
            handleUpdateMetadata={handleUpdateMetadata}
            handleUpdateDraft={handleUpdateDraft}
            handleCancel={handleCancel}
          />
        </Grid>
      </Grid>

      {/* AI Template Generator Dialog */}
      <AITemplateGeneratorDialog
        open={aiGeneratorDialogOpen}
        onClose={() => {
          setSuccess(null); // Clear success state when closing dialog
          setAiGeneratorDialogOpen(false);
        }}
        aiPrompt={aiPrompt}
        generatingTemplate={generatingTemplate}
        setAiPrompt={setAiPrompt}
        handleGenerateTemplate={handleGenerateTemplate}
      />

      {/* Security Policies Dialog */}
      {isEditMode && templateId && templateId !== 'new' && (
        <ContentCheckPolicyManagerDialog
          open={securityPoliciesDialogOpen}
          onClose={() => setSecurityPoliciesDialogOpen(false)}
          templateId={templateId}
        />
      )}
    </Container>
  );
};

export default TemplateEditor;
